import React, { useState, useMemo } from "react";
import Swal from "sweetalert2";
import { useNavigate, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { createExpense } from "../../../services/expenseService";
import AllotImprestModal from "../../components/AllotImprestModal";
import {
  appApi,
  useGetFinanceCategoriesQuery,
  useGetVehiclesQuery,
  useGetContractorsQuery,
  useGetSuppliersQuery,
  useGetExpensesQuery,
  useGetLabourTypesQuery,
  useGetEquipmentListQuery,
} from "../../../store/appApi";
import {
  selectUser,
  selectUserRole,
  selectHasModuleAccess,
} from "../../../store/authSlice";
import { Link } from "react-router-dom";
// Today's date as YYYY-MM-DD in the browser's local time.
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// ─── AddExpenseForm ───────────────────────────────────────────────────────────
// Standalone page for booking a new finance entry (income or expense).
//
// isVehicle flow:
//   Checking the "Vehicle/Machine Expense" checkbox reveals a vehicle
//   dropdown populated from the existing vehicle service. On submit the
//   payload includes { isVehicle: true, vehicleId } which the backend
//   maps to isVehicleExpense + vehicleId on the Finance document.

const AddExpenseForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const [showAllotModal, setShowAllotModal] = useState(false);
  const hasFinanceWrite = useSelector(
    selectHasModuleAccess("finance", "read-write"),
  );

  // ── Remote data for dropdowns ─────────────────────────────────────────────
  // Finance categories (e.g. Fuel, Maintenance, Toll)
  const { data: catData } = useGetFinanceCategoriesQuery();
  const categories = Array.isArray(catData?.list) ? catData.list : [];

  // Vehicles — only fetched / used when isVehicle is checked
  const { data: vehicleData } = useGetVehiclesQuery();
  const vehicles = Array.isArray(vehicleData?.data) ? vehicleData.data : [];

  // Equipment — only used when isVehicle is checked and assetSubType is "equipment"
  const { data: equipmentData } = useGetEquipmentListQuery();
  const equipmentList = equipmentData?.items || [];
  // contractors
  const { data: contractorData } = useGetContractorsQuery();
  const contractors = Array.isArray(contractorData?.data)
    ? contractorData.data
    : [];

  // suppliers
  const { data: supplierData } = useGetSuppliersQuery();
  const suppliers = Array.isArray(supplierData?.suppliers)
    ? supplierData.suppliers
    : [];

  // labour types (individual labourers)
  const { data: labourTypeData } = useGetLabourTypesQuery();
  const labourers = Array.isArray(labourTypeData?.list)
    ? labourTypeData.list
    : Array.isArray(labourTypeData?.labourtype)
      ? labourTypeData.labourtype
      : [];
  // ── Current fund balance — the most recent Day Book entry's chained `balance`
  // (same value the Day Book screen shows). Do NOT re-sum the page-1 entries:
  // that only covers the latest 20 rows and starts from 0, so it under-reports
  // the real fund and wrongly blocks payments.
  const { data: latestExpenseData } = useGetExpensesQuery({ page: 1 });
  const latestEntries = Array.isArray(latestExpenseData?.finance)
    ? latestExpenseData.finance
    : [];
  const currentBalance = useMemo(() => {
    if (!latestEntries.length) return 0;
    const latest = [...latestEntries].sort(
      (a, b) => new Date(b.created) - new Date(a.created),
    )[0];
    return Number(latest?.balance) || 0;
  }, [latestEntries]);

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    type: "payment", // "income" | "expense"
    amount: "",
    categoryId: "",
    paidBy: currentUser?.localId || "", // defaults to logged-in user
    desc: "",
    isVehicle: false,
    isSupplier: false,
    isContractor: false,
    vehicleId: "",
    contractorId: "",
    supplierId: "",
    isLabour: false,
    labourId: "",
    duration: "",
    overtime: "",
    assetSubType: "vehicle", // "vehicle" | "equipment" — sub-option when isVehicle is checked
    equipmentId: "",
    kilometer: "", // odometer reading at the time of a Vehicle expense
    date: todayKey(), // entry date — only a super-admin may pick a past day
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Meter-reading photo — only for a Vehicle/Machine expense (isVehicle +
  // "vehicle" sub-type). The <input> uses accept="image/*" capture="environment"
  // so mobile opens the camera straight away with no gallery option.
  const [meterImage, setMeterImage] = useState(null);
  const [meterPreview, setMeterPreview] = useState("");

  const clearMeterImage = () => {
    setMeterImage(null);
    setMeterPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const handleMeterImage = (e) => {
    const file = e.target.files && e.target.files[0];
    // Clear the value so re-picking the same file still triggers onChange.
    e.target.value = "";
    if (!file) return;
    setMeterImage(file);
    setMeterPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  // ── Labour auto-computed amount (duration × rate + overtime) ─────────────
  // Must sit AFTER useState so `form` is already initialised.
  const selectedLabourer = labourers.find(
    (l) => (l._id || "").toString() === form.labourId,
  );
  const labourAmount = form.isLabour
    ? Number(form.duration || 0) * Number(selectedLabourer?.rate || 0) +
      Number(form.overtime || 0)
    : 0;

  // ── Field handlers ────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setForm((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === "isVehicle" && !checked
        ? { vehicleId: "", equipmentId: "", assetSubType: "vehicle", kilometer: "" }
        : {}),
      ...(name === "assetSubType" && newValue !== "vehicle"
        ? { kilometer: "" }
        : {}),
      ...(name === "isContractor" && !checked ? { contractorId: "" } : {}),
      ...(name === "isSupplier" && !checked ? { supplierId: "" } : {}),
      ...(name === "isLabour" && !checked
        ? { labourId: "", duration: "", overtime: "" }
        : {}),
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // Drop any captured meter photo once the Vehicle/Machine flow is turned
    // off or switched to the Equipment sub-type.
    if (
      (name === "isVehicle" && !checked) ||
      (name === "assetSubType" && newValue !== "vehicle")
    ) {
      clearMeterImage();
    }
  };

  // ── Client-side validation ────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    const effectiveAmount = form.isLabour ? labourAmount : Number(form.amount);
    if (!effectiveAmount || isNaN(effectiveAmount) || effectiveAmount <= 0)
      e.amount = "Enter a valid positive amount.";
    if (form.type !== "receipt" && !form.categoryId)
      e.categoryId = "Category is required.";
    if (!form.type) e.type = "Type is required.";
    if (!form.date) e.date = "Select the entry date.";
    else if (form.date > todayKey()) e.date = "The date can't be in the future.";
    if (form.isVehicle && form.assetSubType === "vehicle" && !form.vehicleId)
      e.vehicleId = "Select a vehicle when Vehicle Expense is checked.";
    if (
      form.isVehicle &&
      form.assetSubType === "equipment" &&
      !form.equipmentId
    )
      e.equipmentId = "Select an equipment when Equipment Expense is checked.";
    if (form.isContractor && !form.contractorId)
      e.contractorId =
        "Select a contractor when Contractor Expense is checked.";
    if (form.isSupplier && !form.supplierId)
      e.supplierId = "Select a supplier when Supplier Expense is checked.";
    if (form.isLabour && !form.labourId)
      e.labourId = "Select a labourer when Labour Payment is checked.";
    if (
      form.isLabour &&
      (!form.duration ||
        isNaN(Number(form.duration)) ||
        Number(form.duration) <= 0)
    )
      e.duration = "Enter a valid duration.";
    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill in all required fields correctly.",
      });
      return;
    }

    // ── Balance guard ─────────────────────────────────────────────────────────
    // Block any debit (non-income) submission that would push the fund below 0.
    // Checks form.type !== "income" to cover both "expense" and "payment" values.
    // A back-dated entry is checked by the server against that day's balance.
    const backDated = form.date && form.date !== todayKey();
    if (form.type !== "receipt" && !backDated) {
      const amountToCheck = form.isLabour ? labourAmount : Number(form.amount);
      if (currentBalance - amountToCheck < 0) {
        Swal.fire({
          icon: "warning",
          title: "Insufficient Balance",
          text: "please add funds negative funds not available.",
          confirmButtonText: "OK",
        });
        return;
      }
    }

    try {
      setLoading(true);

      // When isLabour is active use the pre-computed labourAmount; otherwise use
      // the manually entered form.amount.
      const computedAmount = form.isLabour ? labourAmount : Number(form.amount);

      // Build payload — backend expects isVehicle (boolean) not isVehicleExpense
      const payload = {
        type: form.type,
        amount: computedAmount,
        categoryId: form.categoryId || undefined,
        paidBy: form.paidBy || undefined,
        desc: form.desc,
        date: form.date,
        isVehicle: form.isVehicle && form.assetSubType === "vehicle",
        isEquipment: form.isVehicle && form.assetSubType === "equipment",
        isContractor: form.isContractor,
        isSupplier: form.isSupplier,
        ...(form.isVehicle && form.assetSubType === "vehicle"
          ? {
              vehicleId: form.vehicleId,
              ...(String(form.kilometer).trim() !== ""
                ? { kilometer: Number(form.kilometer) || 0 }
                : {}),
            }
          : {}),
        ...(form.isVehicle && form.assetSubType === "equipment"
          ? { equipmentId: form.equipmentId }
          : {}),
        ...(form.isContractor ? { contractorId: form.contractorId } : {}),
        ...(form.isSupplier ? { supplierId: form.supplierId } : {}),
        isLabour: form.isLabour,
        ...(form.isLabour
          ? {
              labourId: form.labourId,
              overtime: Number(form.overtime) || 0,
            }
          : {}),
      };

      // A captured meter photo forces a multipart request; otherwise the
      // plain JSON payload is sent unchanged.
      const sendMeterPhoto =
        form.isVehicle && form.assetSubType === "vehicle" && meterImage;
      let requestBody = payload;
      if (sendMeterPhoto) {
        const fd = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          fd.append(key, value);
        });
        fd.append("meterImage", meterImage, meterImage.name || "meter.jpg");
        requestBody = fd;
      }

      await createExpense(requestBody);

      // Bust the RTK cache so the expense list refetches automatically
      dispatch(appApi.util.invalidateTags(["Expenses"]));

      Swal.fire({
        icon: "success",
        title: "Entry Posted!",
        text: "Finance entry has been recorded successfully.",
        confirmButtonText: "View Expenses",
      }).then(() => navigate("/payments/payments"));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to post entry. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  if (!hasFinanceWrite) return <Navigate to="/expenses" replace />;
  return (
    <>
      <PageTitle activeMenu="Add Expense" motherMenu="Finance" />

      <div className="row">
        <div className="col-xl-8 col-lg-10 mx-auto">
          <div className="card">
            <div className="card-header d-flex flex-wrap gap-2 align-items-center">
              <h5 className="card-title mb-0">Book Finance Entry</h5>
              <div className="ms-auto d-flex gap-2">
                {isSuperAdmin && (
                  <button
                    type="button"
                    className="card-subtitle btn btn btn-sm btn-outline-dark"
                    onClick={() => setShowAllotModal(true)}
                    disabled={currentBalance <= 0}
                    title={
                      currentBalance <= 0
                        ? "No Daybook balance available to transfer"
                        : undefined
                    }
                  >
                    Allot Imprest
                  </button>
                )}
                <Link
                  style={{ backgroundColor: "#324336" }}
                  className="card-subtitle btn btn btn-sm text-white"
                  to="/payments/payments"
                >
                  Day Book
                </Link>
              </div>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Entry Type <span className="text-danger">*</span>
                      </label>
                      <select
                        name="type"
                        className={`form-control ${errors.type ? "is-invalid" : ""}`}
                        value={form.type}
                        onChange={handleChange}
                      >
                        <option value="payment">Payment</option>
                        <option value="receipt">Receipt</option>
                      </select>
                      {errors.type && (
                        <div className="invalid-feedback">{errors.type}</div>
                      )}
                    </div>
                  </div>
                  {/* ── Category ── */}
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Category
                        {form.type !== "receipt" && (
                          <span className="text-danger"> *</span>
                        )}
                      </label>
                      <select
                        name="categoryId"
                        className={`form-control ${errors.categoryId ? "is-invalid" : ""}`}
                        value={form.categoryId}
                        onChange={handleChange}
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.categoryName}
                          </option>
                        ))}
                      </select>
                      {errors.categoryId && (
                        <div className="invalid-feedback">
                          {errors.categoryId}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {form.type === "payment" && (
                  <>
                    {/* ── isVehicle checkbox ── */}
                    <div className="col-12">
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input
                            id="isVehicle"
                            name="isVehicle"
                            type="checkbox"
                            className="form-check-input"
                            checked={form.isVehicle}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label fw-semibold"
                            htmlFor="isVehicle"
                          >
                            Vehicle / Machine / Equipment Expense
                          </label>
                          <small className="text-muted d-block">
                            Check this to link the expense to a specific vehicle
                            or equipment asset.
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* ── Sub-type radio + dropdown — conditional on isVehicle ── */}
                    {form.isVehicle && (
                      <>
                        <div className="col-12 mb-2">
                          <div className="d-flex gap-4">
                            <div className="custom-control custom-radio">
                              <input
                                type="radio"
                                className="custom-control-input"
                                id="subtype-vehicle"
                                name="assetSubType"
                                value="vehicle"
                                checked={form.assetSubType === "vehicle"}
                                onChange={handleChange}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="subtype-vehicle"
                              >
                                Vehicle
                              </label>
                            </div>
                            <div className="custom-control custom-radio">
                              <input
                                type="radio"
                                className="custom-control-input"
                                id="subtype-equipment"
                                name="assetSubType"
                                value="equipment"
                                checked={form.assetSubType === "equipment"}
                                onChange={handleChange}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="subtype-equipment"
                              >
                                Equipment / Machinery
                              </label>
                            </div>
                          </div>
                        </div>

                        {form.assetSubType === "vehicle" && (
                          <>
                            <div className="col-sm-6">
                              <div className="form-group mb-3">
                                <label className="form-label">
                                  Select Vehicle{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <select
                                  name="vehicleId"
                                  className={`form-control ${errors.vehicleId ? "is-invalid" : ""}`}
                                  style={{ textTransform: "capitalize" }}
                                  value={form.vehicleId}
                                  onChange={handleChange}
                                >
                                  <option value="">Select Vehicle</option>
                                  {vehicles.map((v) => {
                                    const label = [
                                      v.vehicleMake,
                                      v.vehicleModel,
                                    ]
                                      .filter(Boolean)
                                      .join(" ");
                                    return (
                                      <option
                                        key={v._id || v.id}
                                        value={v._id || v.id}
                                        style={{ textTransform: "capitalize" }}
                                      >
                                        {label
                                          ? `${label} — ${v.vehicleNumber}`
                                          : v.vehicleNumber || v._id}
                                      </option>
                                    );
                                  })}
                                </select>
                                {errors.vehicleId && (
                                  <div className="invalid-feedback">
                                    {errors.vehicleId}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* ── Kilometer / odometer reading ── */}
                            <div className="col-sm-6">
                              <div className="form-group mb-3">
                                <label className="form-label">Kilometer</label>
                                <div className="input-group">
                                  <input
                                    type="number"
                                    name="kilometer"
                                    min="0"
                                    step="1"
                                    inputMode="numeric"
                                    className="form-control"
                                    value={form.kilometer}
                                    onChange={handleChange}
                                    placeholder="Odometer reading"
                                  />
                                  <span className="input-group-text">km</span>
                                </div>
                                <small className="text-muted d-block">
                                  Current odometer reading of the vehicle
                                  (optional).
                                </small>
                              </div>
                            </div>

                            {/* ── Meter reading photo — camera only ── */}
                            <div className="col-sm-6">
                              <div className="form-group mb-3">
                                <label className="form-label d-block">
                                  Meter Reading Photo
                                </label>
                                <input
                                  id="meterImageInput"
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  className="d-none"
                                  onChange={handleMeterImage}
                                />
                                {!meterPreview ? (
                                  <label
                                    htmlFor="meterImageInput"
                                    role="button"
                                    className="btn btn-outline-primary mb-0"
                                  >
                                    <i className="fa fa-camera me-2" />
                                    Capture Meter Reading
                                  </label>
                                ) : (
                                  <div className="d-flex align-items-start gap-3">
                                    <img
                                      src={meterPreview}
                                      alt="Meter reading"
                                      style={{
                                        width: 120,
                                        height: 120,
                                        objectFit: "cover",
                                        borderRadius: 8,
                                        border: "1px solid #e5e7eb",
                                      }}
                                    />
                                    <div className="d-flex flex-column gap-2">
                                      <label
                                        htmlFor="meterImageInput"
                                        role="button"
                                        className="btn btn-sm btn-outline-secondary mb-0"
                                      >
                                        Retake
                                      </label>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={clearMeterImage}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <small className="text-muted d-block mt-1">
                                  Opens the camera directly — no gallery
                                  selection.
                                </small>
                              </div>
                            </div>
                          </>
                        )}

                        {form.assetSubType === "equipment" && (
                          <div className="col-sm-6">
                            <div className="form-group mb-3">
                              <label className="form-label">
                                Select Equipment{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                name="equipmentId"
                                className={`form-control ${errors.equipmentId ? "is-invalid" : ""}`}
                                value={form.equipmentId}
                                onChange={handleChange}
                              >
                                <option value="">Select Equipment</option>
                                {equipmentList.map((eq) => (
                                  <option key={eq._id} value={eq._id}>
                                    {eq.eqpName}
                                    {eq.eqpNumber ? ` (${eq.eqpNumber})` : ""}
                                    {" — "}
                                    {eq.projectName || "No project"}
                                  </option>
                                ))}
                              </select>
                              {errors.equipmentId && (
                                <div className="invalid-feedback">
                                  {errors.equipmentId}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* ── isContractor checkbox ── */}
                    {/* <div className="col-12">
                      <div className="form-group mb-3">
                        <div
                          className="form-check"
                          style={{ borderWidth: "12px", borderColor: "red" }}
                        >
                          <input
                            id="isContractor"
                            name="isContractor"
                            type="checkbox"
                            className="form-check-input"
                            checked={form.isContractor}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label fw-semibold"
                            htmlFor="isContractor"
                          >
                            Contractor Expense
                          </label>
                          <small className="text-muted d-block">
                            Check this to link the expense to a specific
                            contractor and generate a matching contractor log
                            entry.
                          </small>
                        </div>
                      </div>
                    </div> */}

                    {/* ── CONTRACTOR dropdown — conditional on isContractor ── */}
                    {/* {form.isContractor && (
                      <div className="col-sm-6">
                        <div className="form-group mb-3">
                          <label className="form-label">
                            Select Contractor{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            name="contractorId"
                            className={`form-control ${errors.contractorId ? "is-invalid" : ""}`}
                            value={form.contractorId}
                            onChange={handleChange}
                          >
                            <option value="">Select Contractor</option>
                            {contractors.map((c) => (
                              <option key={c._id || c.id} value={c._id || c.id}>
                                {c?.name + " — " + c?.catName ||
                                  c.name ||
                                  c._id ||
                                  "Unnamed Contractor"}
                              </option>
                            ))}
                          </select>
                          {errors.contractorId && (
                            <div className="invalid-feedback">
                              {errors.contractorId}
                            </div>
                          )}
                        </div>
                      </div>
                    )} */}

                    {/* ── isSupplier checkbox ── */}
                    {/* <div className="col-12">
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input
                            id="isSupplier"
                            name="isSupplier"
                            type="checkbox"
                            className="form-check-input"
                            checked={form.isSupplier}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label fw-semibold"
                            htmlFor="isSupplier"
                          >
                            Supplier Expense
                          </label>
                          <small className="text-muted d-block">
                            Check this to link the expense to a specific
                            supplier and generate a matching supplier log entry.
                          </small>
                        </div>
                      </div>
                    </div> */}

                    {/* ── Supplier dropdown — conditional on isSupplier ── */}
                    {/* {form.isSupplier && (
                      <div className="col-sm-6">
                        <div className="form-group mb-3">
                          <label className="form-label">
                            Select Supplier{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            name="supplierId"
                            className={`form-control ${errors.supplierId ? "is-invalid" : ""}`}
                            value={form.supplierId}
                            onChange={handleChange}
                          >
                            <option value="">Select Supplier</option>
                            {suppliers.map((s) => (
                              <option key={s._id || s.id} value={s._id || s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                          {errors.supplierId && (
                            <div className="invalid-feedback">
                              {errors.supplierId}
                            </div>
                          )}
                        </div>
                      </div>
                    )} */}
                    {/* ── isLabour checkbox ── */}
                    <div className="col-12">
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input
                            id="isLabour"
                            name="isLabour"
                            type="checkbox"
                            className="form-check-input"
                            checked={form.isLabour}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label fw-semibold"
                            htmlFor="isLabour"
                          >
                            Labour Payment
                          </label>
                          <small className="text-muted d-block">
                            Check this to link the payment to a specific
                            labourer. Amount will be auto-calculated from
                            duration × rate + overtime.
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* ── Labour conditional fields ── */}
                    {form.isLabour &&
                      (() => {
                        const selected = labourers.find(
                          (l) => (l._id || "").toString() === form.labourId,
                        );
                        const base =
                          Number(form.duration || 0) *
                          Number(selected?.rate || 0);
                        const total = base + Number(form.overtime || 0);
                        const durationLabel =
                          selected?.typeOfLabour === "daily"
                            ? "How many days?"
                            : selected?.typeOfLabour === "monthly"
                              ? "How many months?"
                              : selected?.typeOfLabour === "hourly"
                                ? "How many hours?"
                                : "Duration";

                        return (
                          <>
                            {/* Labourer dropdown */}
                            <div className="col-sm-6">
                              <div className="form-group mb-3">
                                <label className="form-label">
                                  Select Labourer{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <select
                                  name="labourId"
                                  className={`form-control ${errors.labourId ? "is-invalid" : ""}`}
                                  value={form.labourId}
                                  onChange={handleChange}
                                >
                                  <option value="">Select Labourer</option>
                                  {labourers.map((l) => (
                                    <option
                                      key={l._id || l.id}
                                      value={l._id || l.id}
                                    >
                                      {l.name} — {l.laborCat}
                                    </option>
                                  ))}
                                </select>
                                {errors.labourId && (
                                  <div className="invalid-feedback">
                                    {errors.labourId}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Rate & type info (read-only) */}
                            {selected && (
                              <div className="col-sm-6">
                                <div className="form-group mb-3">
                                  <label className="form-label">
                                    Rate / Type (auto-fetched)
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    readOnly
                                    value={`₹ ${selected.rate || 0} / ${selected.typeOfLabour || "—"}`}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Duration */}
                            <div className="col-sm-6">
                              <div className="form-group mb-3">
                                <label className="form-label">
                                  {durationLabel}{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  name="duration"
                                  type="number"
                                  min="0"
                                  className={`form-control ${errors.duration ? "is-invalid" : ""}`}
                                  placeholder="e.g. 5"
                                  value={form.duration}
                                  onChange={handleChange}
                                />
                                {errors.duration && (
                                  <div className="invalid-feedback">
                                    {errors.duration}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Overtime */}
                            <div className="col-sm-6">
                              <div className="form-group mb-3">
                                <label className="form-label">
                                  Overtime Amount (₹)
                                </label>
                                <input
                                  name="overtime"
                                  type="number"
                                  min="0"
                                  className="form-control"
                                  placeholder="e.g. 200"
                                  value={form.overtime}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>

                            {/* Computed total */}
                            <div className="col-12">
                              <div className="alert alert-info py-2 mb-3">
                                <strong>Amount Payable:</strong> ₹{" "}
                                {(
                                  base + Number(form.overtime || 0)
                                ).toLocaleString("en-IN")}
                                <span className="text-muted ms-2 small">
                                  ({form.duration || 0} × ₹{selected?.rate || 0}{" "}
                                  + ₹{form.overtime || 0} overtime)
                                </span>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                  </>
                )}
                <div className="row">
                  {/* ── Type ── */}

                  {/* ── Amount ── */}
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Amount (₹){" "}
                        {!form.isLabour && (
                          <span className="text-danger">*</span>
                        )}
                        {form.isLabour && (
                          <span className="text-muted small fw-normal ms-1">
                            (auto-calculated)
                          </span>
                        )}
                      </label>
                      <input
                        name="amount"
                        type="number"
                        min="1"
                        className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                        placeholder="Enter amount"
                        value={form.isLabour ? labourAmount : form.amount}
                        onChange={handleChange}
                        disabled={form.isLabour}
                      />
                      {errors.amount && (
                        <div className="invalid-feedback">{errors.amount}</div>
                      )}
                    </div>
                  </div>

                  {/* ── Entry Date ── past days are super-admin only ── */}
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Date <span className="text-danger">*</span>
                      </label>
                      <input
                        name="date"
                        type="date"
                        className={`form-control ${errors.date ? "is-invalid" : ""}`}
                        value={form.date}
                        max={todayKey()}
                        onChange={handleChange}
                        disabled={!isSuperAdmin}
                      />
                      {errors.date && (
                        <div className="invalid-feedback">{errors.date}</div>
                      )}
                      {isSuperAdmin && form.date !== todayKey() && (
                        <small className="text-muted">
                          Back-dated: later balances will be recalculated.
                        </small>
                      )}
                    </div>
                  </div>

                  {/* ── Paid By (optional override) ── */}
                  {/* <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Paid By (User ID)</label>
                      <input
                        name="paidBy"
                        type="text"
                        className="form-control"
                        placeholder="User ID (defaults to you)"
                        value={form.paidBy}
                        onChange={handleChange}
                      />
                      <small className="text-muted">
                        Leave blank to record against your account.
                      </small>
                    </div>
                  </div> */}

                  {/* ── Description ── */}
                  <div className="col-12">
                    <div className="form-group mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        name="desc"
                        className="form-control"
                        rows={3}
                        placeholder="Add notes or remarks…"
                        value={form.desc}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* ── Actions ── */}
                  <div className="col-12 mt-2">
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Posting…
                        </>
                      ) : (
                        "Post Entry"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={() => navigate("/payments/payments")}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <AllotImprestModal
        show={showAllotModal}
        onClose={() => setShowAllotModal(false)}
        currentBalance={currentBalance}
      />
    </>
  );
};

export default AddExpenseForm;
